import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts'
import {
  TrendingUp,
  DollarSign,
  ShoppingCart,
  Users,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Building2,
  Trophy
} from 'lucide-react'
import { useCurrency } from '@/hooks/useCurrency'
import { ApiService } from '@/services/api'

interface RestaurantBreakdown {
  restaurantId: string
  name: string
  dailyRevenue: number
  dailyBillsCount: number
  monthlyRevenue: number
  averageBillValue: number
}

// Stable, distinct color per restaurant — reused across both charts and the table's color dots.
const RESTAURANT_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#f43f5e', '#14b8a6', '#f97316', '#84cc16']
const colorForIndex = (i: number) => RESTAURANT_COLORS[i % RESTAURANT_COLORS.length]

const CustomTooltip = ({ active, payload, label }: any) => {
  const { formatCurrency } = useCurrency()
  if (!active || !payload || !payload.length) return null
  return (
    <div className="rounded-lg border bg-background px-3 py-2 shadow-sm text-xs">
      <p className="font-medium mb-1">{label}</p>
      {payload.map((entry: any, i: number) => (
        <p key={i} style={{ color: entry.color }}>
          {entry.name}: {entry.name?.toLowerCase().includes('order') ? entry.value : formatCurrency(entry.value)}
        </p>
      ))}
    </div>
  )
}

interface CombinedDashboardMetrics {
  dailyRevenue: number
  monthlyRevenue: number
  dailyBillsCount: number
  monthlyBillsCount: number
  averageBillValue: number
  peakHour: number
  byRestaurant: RestaurantBreakdown[]
}

export default function CombinedDashboardScreen() {
  const { formatCurrency, currencySymbol } = useCurrency()
  const [metrics, setMetrics] = useState<CombinedDashboardMetrics | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadMetrics = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await ApiService.getCombinedDashboardMetrics()
      if (response.success) {
        setMetrics(response.data)
      } else {
        setError(response.message || 'Failed to load combined dashboard metrics')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load combined dashboard metrics')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadMetrics()
  }, [])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2 text-muted-foreground">Loading combined dashboard...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {error}
            <Button variant="outline" size="sm" className="ml-2 py-[10px]" onClick={loadMetrics}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  if (!metrics) return null

  const sortedByRevenue = [...metrics.byRestaurant].sort((a, b) => (b.dailyRevenue || 0) - (a.dailyRevenue || 0))
  const topRestaurantId = sortedByRevenue[0]?.restaurantId
  const colorByRestaurantId = new Map(sortedByRevenue.map((r, i) => [r.restaurantId, colorForIndex(i)]))

  const metricCards = [
    { title: 'Daily Revenue', value: formatCurrency(metrics.dailyRevenue || 0), description: "Today's total across all restaurants", icon: DollarSign },
    { title: 'Monthly Revenue', value: formatCurrency(metrics.monthlyRevenue || 0), description: "This month's total across all restaurants", icon: TrendingUp },
    { title: 'Daily Orders', value: (metrics.dailyBillsCount || 0).toString(), description: 'Orders completed today', icon: ShoppingCart },
    { title: 'Average Order Value', value: formatCurrency(metrics.averageBillValue || 0), description: 'Average per order, combined', icon: Users },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">All Restaurants</h1>
          <p className="text-muted-foreground text-sm sm:text-base">Combined performance across every restaurant you own</p>
        </div>
        <Button onClick={loadMetrics} variant="outline" size="icon" className="py-[10px] sm:w-auto sm:h-auto sm:px-4 flex-shrink-0">
          <RefreshCw className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      <div className="grid gap-2 sm:gap-4 grid-cols-2 lg:grid-cols-4">
        {metricCards.map((card) => (
          <Card key={card.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{card.title}</CardTitle>
              <card.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-xl sm:text-2xl font-bold">{card.value}</div>
              <p className="text-xs text-muted-foreground">{card.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Revenue by Restaurant</CardTitle>
            <CardDescription>Today's revenue, ranked</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={Math.max(180, sortedByRevenue.length * 45)}>
              <BarChart data={sortedByRevenue} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }} barCategoryGap="30%">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${currencySymbol}${(v / 1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={90} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="dailyRevenue" name="Revenue" radius={[0, 4, 4, 0]}>
                  {sortedByRevenue.map((r) => (
                    <Cell key={r.restaurantId} fill={colorByRestaurantId.get(r.restaurantId)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Orders by Restaurant</CardTitle>
            <CardDescription>Today's order count, ranked</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={Math.max(180, sortedByRevenue.length * 45)}>
              <BarChart data={sortedByRevenue} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }} barCategoryGap="30%">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5e7eb" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={90} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="dailyBillsCount" name="Orders" radius={[0, 4, 4, 0]}>
                  {sortedByRevenue.map((r) => (
                    <Cell key={r.restaurantId} fill={colorByRestaurantId.get(r.restaurantId)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="h-4 w-4" />
            By Restaurant
          </CardTitle>
          <CardDescription>Today's revenue and orders, broken down per restaurant</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Restaurant</TableHead>
                <TableHead className="text-right">Daily Revenue</TableHead>
                <TableHead className="text-right">% of Total</TableHead>
                <TableHead className="text-right">Daily Orders</TableHead>
                <TableHead className="text-right">Monthly Revenue</TableHead>
                <TableHead className="text-right">Avg Order Value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedByRevenue.map((r) => {
                const share = metrics.dailyRevenue > 0 ? ((r.dailyRevenue || 0) / metrics.dailyRevenue) * 100 : 0
                return (
                  <TableRow key={r.restaurantId}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: colorByRestaurantId.get(r.restaurantId) }}
                        />
                        {r.name}
                        {r.restaurantId === topRestaurantId && (
                          <Badge variant="secondary" className="gap-1 text-xs">
                            <Trophy className="h-3 w-3" />
                            Top
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{formatCurrency(r.dailyRevenue || 0)}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{share.toFixed(1)}%</TableCell>
                    <TableCell className="text-right">{r.dailyBillsCount || 0}</TableCell>
                    <TableCell className="text-right">{formatCurrency(r.monthlyRevenue || 0)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(r.averageBillValue || 0)}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}
